import { safeSum } from 'Pmi/utils';

export const pmiHeroChartDataConverter = {
	convert(data, params) {
		let { nonAddressableData, addressableData } = data;
		const { categoryLevel, categoryName } = params;

		const result = {
			categoryLevel,
			categoryName,
			hierarchy: getHierarchy(params) || [],
			addressable: getConvertedList(addressableData, params),
			nonAddressable: getConvertedList(nonAddressableData, params),
		};

		return result;
	},
};

export const getConvertedList = (list, params) => {
	const { isTailSpend, showTailSpend } = params;

	const parentId = getParentId(params);
	let filteredList = getFilteredListByParentId(list, parentId);

	if (isTailSpend) {
		let tailSpendFilteredList = getFilteredTailSpendListByParentId(
			list,
			parentId
		);

		// show tail spend data only
		filteredList = tailSpendFilteredList;
	} else if (!showTailSpend) {
		// add to each of the category tail spend value
		let tailSpendFilteredList = getFilteredTailSpendListByParentId(
			list,
			parentId
		);

		filteredList = filteredList.map(item => {
			const tailItem = tailSpendFilteredList.find(
				tailItem => tailItem.id === item.id
			);

			if (tailItem) {
				return getGroupedItem([item, tailItem]);
			}

			return item;
		});
	}

	// first convert - value is calculated based on splitByDimension
	filteredList = filteredList.map(convertListItem);

	// and then sort by value - large first
	const valueSorter = ({ value: a }, { value: b }) => b - a;
	filteredList.sort(valueSorter);

	// add after sorting - do groupping
	filteredList = groupToOthers(filteredList, params);

	if (showTailSpend && !isTailSpend) {
		let tailSpendParentItem = getTailSpendParentItem(list, params);

		if (tailSpendParentItem) {
			// convert it - value is calculated based on splitByDimension
			tailSpendParentItem = convertListItem(tailSpendParentItem);
			// include tail spend as separate category
			filteredList = [...filteredList, tailSpendParentItem];
		}
	}

	return filteredList;
};

const GROUP_ITEM_NAME = 'Others';
export const groupToOthers = (list, params) => {
	const {
		othersThreshold: valueThreshold,
		othersCountThreshold: countThreshold,
		showAllCategories,
	} = params;

	if (showAllCategories) {
		return list;
	}
	// prefer countThreshold over valueThreshold
	const visibleSubList = countThreshold
		? list.slice(0, countThreshold - 1)
		: list.filter(item => item.value >= valueThreshold);

	// if we have 2 or more items to group
	if (list.length - visibleSubList.length >= 2) {
		// prefer countThreshold over valueThreshold
		const groupedSubList = countThreshold
			? list.slice(countThreshold - 1)
			: list.filter(dataItem => Math.abs(dataItem.value) < valueThreshold);

		const groupItem = getGroupedItem(groupedSubList);
		const { splitByDimension } = groupItem;
		// recalculate value based on
		let value = getValueFromSplitByDimention(splitByDimension);

		value = roundWith2Decimals(value);

		return [
			...visibleSubList,
			{
				name: GROUP_ITEM_NAME,
				isGrouped: true,
				splitByDimension,
				value,
			},
		];
	}

	return list;
};

export const convertListItem = item => {
	const { label, id, splitByDimension, isTailVendorsNode } = item;

	let value = getValueFromSplitByDimention(splitByDimension);

	value = roundWith2Decimals(value);

	const result = {
		id,
		name: label,
		value,
		splitByDimension,
	};

	if (isTailVendorsNode) {
		result.isTailSpend = true;
	}

	return result;
};

export const getFilteredTailSpendList = (list, params) => {
	const parentId = getParentId(params);

	return getFilteredTailSpendListByParentId(list, parentId);
};

export const getFilteredTailSpendListByParentId = (list, parentId) => {
	const filteredList = getFilteredListByParentId(list, parentId);
	const ids = filteredList.map(item => item.id);

	const filteredTailItemsList = list.filter(
		item => item.isTailVendorsNode && ids.includes(item.parentId)
	);

	return filteredList
		.map(item => {
			const tailItem = filteredTailItemsList.find(
				tailItem => tailItem.id === `${item.id}tailSpend/`
			);

			return tailItem
				? {
						...item,
						splitByDimension: tailItem.splitByDimension,
						value: tailItem.value,
				  }
				: null;
		})
		.filter(Boolean);
};

export const getFilteredList = (list, params, isTailVendorsNode = false) => {
	const parentId = getParentId(params);

	return getFilteredListByParentId(list, parentId, isTailVendorsNode);
};

export const getFilteredListByParentId = (
	list,
	parentId,
	isTailVendorsNode = false
) => {
	const filteredList = list.filter(
		item =>
			decodedEqual(item.parentId, parentId) &&
			item.isTailVendorsNode === isTailVendorsNode
	);

	return filteredList;
};

export const getTailSpendParentItem = (list, params) => {
	return getParentItem(list, params, true);
};

const getParentItem = (list, params, isTailVendorsNode = false) => {
	let parentId = getParentId(params);

	if (isTailVendorsNode) {
		parentId += 'tailSpend/';
	}

	const filteredList = list.filter(
		item =>
			decodedEqual(item.id, parentId) &&
			item.isTailVendorsNode === isTailVendorsNode
	);

	return filteredList[0];
};

export const getGroupedItem = list => {
	if (!list || list.length === 0) {
		return {
			value: 0,
			splitByDimension: {},
		};
	}

	const splitByDimension = list.reduce((acc, item) => {
		const { splitByDimension } = item;
		const keys = [
			...new Set([...Object.keys(splitByDimension), ...Object.keys(acc)]),
		];

		const result = keys.reduce(
			(result, key) => ({
				...result,
				[key]: roundWith2Decimals(safeSum(splitByDimension[key], acc[key])),
			}),
			{}
		);
		return result;
	}, {});

	return {
		...list[0],
		splitByDimension,
	};
};

export const roundWith2Decimals = num => {
	return Math.round(num * 100) / 100;
};

export const getValueFromSplitByDimention = splitByDimension => {
	const keys = Object.keys(splitByDimension || {});

	const sum = keys.reduce((acc, key) => safeSum(splitByDimension[key], acc), 0);

	return sum;
};

const getHierarchy = params => {
	let { hierarchy, categoryName } = params;

	if (hierarchy) {
		hierarchy = !Array.isArray(hierarchy) ? [hierarchy] : [...hierarchy];
	} else {
		hierarchy = [];
	}

	if (categoryName) {
		hierarchy.push(categoryName);
	}

	return hierarchy;
};

export const getParentId = params => {
	if (params.overrideParentId) {
		return params.overrideParentId;
	}

	let hierarchyString = '';
	const hierarchy = getHierarchy(params);

	hierarchyString = hierarchy.map(encodeURIComponent).join('/');
	hierarchyString = hierarchyString ? '/' + hierarchyString : hierarchyString;

	return `${hierarchyString}/`;
};

export const decodedEqual = (a, b) => {
	return decodeURIComponent(a) === decodeURIComponent(b);
};
